import { createFileRoute, Navigate } from '@tanstack/react-router';
import { AppContext } from '@/app/appContext';
import { Logo } from '@/app/ui/components/logo';
import { Button } from '@/app/ui/components/button';
import { Card } from '@/app/ui/components/card';
import { Form } from '@/app/ui/components/form';
import { Field, FieldControl, FieldError, FieldLabel } from '@/app/ui/components/form/field';
import { createOrganization } from '@/app/services/organizationService';
import { queryKeys } from '@/app/utils/constants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useTranslations } from 'use-intl';
import * as React from 'react';
import bg from '@/app/assets/bg.png';
import { useGetSharepointConnectionStatus } from '@/app/hooks/queries/useSharepoint';
import { useSharepointLoginBtn } from '@/app/components/features/sharepoint/useSharepointLoginBtn';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowRight02Icon, Tick02Icon } from '@hugeicons/core-free-icons';
import { updateUserProfile } from '@/app/services/userService';
import { Fieldset, FieldsetLegend } from '@/app/ui/components/form/fieldset';
import { Spinner } from '@/app/ui/components/spinner';
import { useAuth } from '@/app/utils/auth';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/app/ui/components/alert-dialog';
import { Icons } from '@/app/ui/components/icons';

export const Route = createFileRoute('/_authed/(membership)/add-organization')({
  component: RouteComponent,
});

const stepAtom = atomWithStorage('add_organization_step', 0);
const organizationNameAtom = atom('');

function RouteComponent() {
  const t = useTranslations('pages.organization');
  const tAll = useTranslations();
  const { userMeta } = React.useContext(AppContext);
  const step = useAtomValue(stepAtom);
  const { logout } = useAuth();

  const hasAnyOrganization = userMeta.organizations.length > 0;
  const currentOrg = userMeta.organizations.find(
    (org) => org.organizationId === userMeta.organizationId,
  );
  const organizationEnabled = !!currentOrg?.isEnabled;

  if (hasAnyOrganization && organizationEnabled) return <Navigate to="/" />;

  if (hasAnyOrganization && !organizationEnabled) {
    return (
      <div className="px-5 -translate-y-5 w-full my-auto">
        <Logo className="mb-5 mx-auto" />
        <Card className="max-w-92 space-y-2 lg:p-6 p-5 mx-auto w-full">
          <p className="text-lg text-center font-semibold mb-5">
            {t('alerts.pendingActivation.alertHeader')}
          </p>
          <p className="text-left">{t('alerts.pendingActivation.thankYou')}</p>
          <p className="text-left">{t('alerts.pendingActivation.accountReviewed')}</p>
          <p className="text-left">{t('alerts.pendingActivation.confirmationMsg')}</p>
          <Button variant="secondary" onClick={() => logout()} className="w-full mt-4" size="lg">
            {tAll('components.headernav.logout')}
          </Button>
        </Card>
      </div>
    );
  }

  const content = [<UserDetailsStep key="0" />, <OrganizationStep key="1" />];

  return (
    <div className="grid lg:grid-cols-[30%_1fr] h-svh grow">
      <div className="p-5 md:p-7 flex flex-col">
        <Logo className="mb-5 md:mb-8" />
        <p className="font-medium text-muted mb-5">
          <strong className="text-foreground"> {step + 1}</strong> / {content.length}
        </p>
        {content[step]}
      </div>
      <img src={bg} alt="" className="size-full h-svh max-lg:hidden object-cover" />
    </div>
  );
}

function UserDetailsStep() {
  const tGeneral = useTranslations('general');
  const t = useTranslations('pages.organization');
  const { userMeta } = React.useContext(AppContext);
  const setStep = useSetAtom(stepAtom);
  const [firstName, setFirstName] = React.useState(userMeta.firstName || '');
  const [lastName, setLastName] = React.useState(userMeta.lastName || '');

  React.useEffect(() => {
    if (firstName === '' && lastName === '') {
      setFirstName(userMeta.firstName || '');
      setLastName(userMeta.lastName || '');
    }
  }, [firstName, lastName, userMeta]);

  const { mutate, isPending } = useMutation({
    mutationFn: async (payload: { firstName: string; lastName: string }) => {
      await updateUserProfile({
        ...payload,
        systemLanguage: userMeta.systemLanguage,
        defaultTranslationLanguage: userMeta.defaultTranslationLanguage,
      });
    },
    onSuccess: () => {
      setStep((prev) => prev + 1);
    },
  });

  return (
    <>
      <Form
        onFormSubmit={() => {
          if (firstName === userMeta.firstName && lastName === userMeta.lastName) {
            setStep((prev) => prev + 1);
            return;
          }
          mutate({ firstName, lastName });
        }}
        className="space-y-3 md:space-y-5 grow flex flex-col"
      >
        <Fieldset>
          <FieldsetLegend>{t('userDetails.legend')}</FieldsetLegend>
          <Field>
            <FieldLabel>{t('formLabels.firstName')}</FieldLabel>
            <FieldControl
              value={firstName}
              onValueChange={setFirstName}
              required
              placeholder={t('formLabels.firstName')}
            />
            <FieldError />
          </Field>
          <Field>
            <FieldLabel>{t('formLabels.lastName')}</FieldLabel>
            <FieldControl
              value={lastName}
              onValueChange={setLastName}
              required
              placeholder={t('formLabels.lastName')}
            />
            <FieldError />
          </Field>
          <Field>
            <FieldLabel>{t('formLabels.email')}</FieldLabel>
            <FieldControl value={userMeta.email} readOnly placeholder={t('formLabels.email')} />
          </Field>
        </Fieldset>
        <Button type="submit" size="lg" className="w-full mt-auto" pending={isPending}>
          {tGeneral('continue')}
          <HugeiconsIcon icon={ArrowRight02Icon} />
        </Button>
      </Form>
    </>
  );
}

function OrganizationStep() {
  const tGeneral = useTranslations('general');
  const t = useTranslations('pages.organization');
  const queryClient = useQueryClient();
  const setStep = useSetAtom(stepAtom);
  const [organizationName, setOrganizationName] = useAtom(organizationNameAtom);
  const logIn = useSharepointLoginBtn();
  const { data, isLoading } = useGetSharepointConnectionStatus();

  const { mutate, isPending } = useMutation({
    mutationFn: createOrganization,
    onSuccess: () => {
      setStep(0);
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.IS_USER_ON_BOARD],
        refetchType: 'all',
      });
    },
  });

  const isConnectedToSharepoint = data?.connected;

  return (
    <>
      <Form
        onFormSubmit={() => mutate({ name: organizationName })}
        className="space-y-3 md:space-y-5 grow flex flex-col"
      >
        <Fieldset>
          <FieldsetLegend>{t('orgSetup.legend')}</FieldsetLegend>
          <Field>
            <FieldLabel>{t('formLabels.organizationName')}</FieldLabel>
            <FieldControl
              value={organizationName}
              onValueChange={setOrganizationName}
              required
              placeholder={t('formLabels.organizationName')}
              maxLength={60}
            />
            <FieldError />
          </Field>
        </Fieldset>

        <div className="space-y-2">
          <p className="text-sm mb-4">{t('orgSetup.documentManagement')}</p>

          {isLoading ? (
            <div className="py-4">
              <Spinner />
            </div>
          ) : (
            <div className="flex gap-4 sm:items-center max-sm:flex-col">
              <div className="flex gap-3 items-center">
                <div className="bg-surface-3 rounded-md grid size-10 place-items-center">
                  <Icons.sharepoint className="size-6" />
                </div>
                <div>
                  <p className="font-medium text-sm">{t('orgSetup.sharepoint.title')}</p>
                  <p className="text-muted text-sm">
                    {isConnectedToSharepoint
                      ? t('orgSetup.sharepoint.connectedAs', { displayName: data.displayName })
                      : t('orgSetup.sharepoint.allowAccess')}
                  </p>
                </div>
              </div>

              {isConnectedToSharepoint ? (
                <Button
                  disabled
                  variant="secondary"
                  kind="with-icon"
                  className="sm:ml-auto max-sm:w-full"
                >
                  <HugeiconsIcon icon={Tick02Icon} />
                  {tGeneral('connected')}
                </Button>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger
                    render={
                      <Button className="sm:ml-auto max-sm:w-full">{tGeneral('connect')}</Button>
                    }
                  />
                  <AlertDialogPopup>
                    <AlertDialogTitle>{t('orgSetup.sharepoint.dialogTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('orgSetup.sharepoint.dialogDescription')}
                    </AlertDialogDescription>
                    <AlertDialogFooter>
                      <AlertDialogCancel />
                      <Button onClick={logIn}>{tGeneral('connect')}</Button>
                    </AlertDialogFooter>
                  </AlertDialogPopup>
                </AlertDialog>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 mt-auto gap-2">
          <Button
            type="button"
            size="lg"
            variant="secondary"
            onClick={() => setStep((prev) => prev - 1)}
          >
            {tGeneral('back')}
          </Button>
          <Button type="submit" size="lg" pending={isPending}>
            {tGeneral('continue')}
            <HugeiconsIcon icon={ArrowRight02Icon} />
          </Button>
        </div>
      </Form>
    </>
  );
}
